import { NotificationsNone } from '@mui/icons-material'
import { Badge } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { authState } from '../../state/auth-state'
import { colors } from '../../constants/colors'
import {
  fetchNotifications,
  subscribeToNotificationPreferences,
  subscribeToNotifications
} from '../../async/supabase/notifications'
import { getAccount } from '../../async/account'
import { CircleButton, SquareButton } from '../../elements/basic-elements'
import { ResponsiveTrayModal } from '../modals/top-drawer-modal'
import NotificationTray from './notification-tray'
import PushDeviceCheck from './push-device-check'
import { OPEN_NOTIFICATIONS_EVENT, OpenNotificationsEventDetail } from './open-notifications-panel'

/**
 * Self-contained notification bell: renders a bell button with an unread badge,
 * keeps the unread count live via the Supabase realtime subscription, and opens
 * the notification tray in a modal. Drop a single <NotificationBell /> into a
 * header — all state/wiring is encapsulated here.
 */
const NotificationBell = ({
  circular,
  dotBadge
}: {
  circular?: boolean
  /** Show a small unread dot instead of the unread count */
  dotBadge?: boolean
}) => {
  const { account, authUser, setAccount } = authState()
  const [trayOpen, setTrayOpen] = useState(false)
  // Bumped on each "open to settings" request so repeat requests re-open settings
  const [settingsRequest, setSettingsRequest] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)

  // Other pages can open the panel via openNotificationsPanel()
  useEffect(() => {
    if (!account) return
    const handleOpen = (event: Event) => {
      const { view } = (event as CustomEvent<OpenNotificationsEventDetail>).detail || {}
      setTrayOpen(true)
      if (view === 'settings') setSettingsRequest(request => request + 1)
    }
    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpen)
  }, [account])

  useEffect(() => {
    if (!account?.id) {
      setUnreadCount(0)
      return
    }
    const refreshUnread = async () => {
      const notifications = await fetchNotifications()
      setUnreadCount(notifications.filter(n => !n.read).length)
    }
    void refreshUnread()
    const unsubscribe = subscribeToNotifications(() => {
      void refreshUnread()
    })
    return () => unsubscribe()
  }, [account?.id])

  // Keep notification preferences live: they can change outside the settings UI
  // (email unsubscribe, a mail client's one-click unsubscribe, another tab/device).
  // On any change, reload the account so the settings panel reflects it.
  const authUid = authUser?.uid
  useEffect(() => {
    if (!account?.id || !authUid) return
    const unsubscribe = subscribeToNotificationPreferences(() => {
      void getAccount(authUid)
        .then(refreshed => {
          if (refreshed) setAccount(refreshed)
        })
        .catch(err => console.error('Failed to refresh notification preferences', err))
    })
    return () => unsubscribe()
  }, [account?.id, authUid])

  if (!account) return null

  const ButtonComponent = circular ? CircleButton : SquareButton

  const closeTray = () => {
    setTrayOpen(false)
    // Reset so opening from the bell later shows the list, not settings
    setSettingsRequest(0)
  }

  return (
    <>
      <ButtonComponent
        variant="contained"
        color="tertiary"
        disableElevation
        onClick={() => setTrayOpen(true)}
        aria-label="Notifications"
      >
        <Badge
          badgeContent={unreadCount}
          color="primary"
          max={9}
          variant={dotBadge ? 'dot' : 'standard'}
          sx={dotBadge ? { '& .MuiBadge-dot': { bgcolor: colors.RED_100 } } : undefined}
        >
          <NotificationsNone sx={{ color: theme => theme.palette.text.primary }} />
        </Badge>
      </ButtonComponent>

      {/* Push is on for the account but this device can't receive it → fix/ask */}
      <PushDeviceCheck
        accountId={account.id}
        pushEnabled={account.user?.settings?.notifications?.push_notifications_enabled === true}
      />

      <ResponsiveTrayModal open={trayOpen} onClose={closeTray}>
        <NotificationTray
          userId={account.id}
          settingsRequest={settingsRequest}
          notificationSettings={account.user?.settings?.notifications}
          notificationPrivacy={account.user?.settings?.notification_privacy}
          onClose={closeTray}
        />
      </ResponsiveTrayModal>
    </>
  )
}

export default NotificationBell
